import {
	Box,
	Button,
	MenuItem,
	Select,
	TextField,
	Typography
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { ColorScheme } from "../../Themes/ColorScheme";
import { ApiContext } from "../ApiContext";
import { Spinner } from "../Common/Spinner";

import { isDesktop, isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { downloadCsvFromJson } from "../../Helpers/JsonToCsvDownload";
import { downloadExcelFromJson } from "../../Helpers/JsonToExcelDownload";
import LandingAnimation from "../../Helpers/LandingAnimation";
import PagingIndicator from "../Common/PagingIndicator";
import { UserContext } from "../UserContext";
import { ClientCard } from "./ClientCard";
import { ClientOrders } from "./ClientOrders";
import { ClientSortingOptions } from "./ClientSortingOptions";

const ONE_PAGE_LIMTI = 20;

export const ClientContent = () => {
	const { t } = useTranslation();

	const { selectedLocation, handleTitle } = useContext(UserContext);

	const { ApiCall } = useContext(ApiContext);

	const [search, setSearch] = useState("");

	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(200);

	const [loading, setLoading] = useState(true);
	const [content, setContent] = useState([]);
	const [boxHeight, setBoxHeight] = useState(80);

	const [readyToFetch, setReadyToFetch] = useState(false);

	const [sort, setSort] = useState("name");

	const [descending, setDescending] = useState(false);

	const [dialogUser, setDialogUser] = useState(null);

	const [loadingUserCsv, setLoadingUserCsv] = useState(false);
	const [fileType, setFileType] = useState("csv");

	function fetchClientData() {
		setContent([]);
		ApiCall(
			"GET",
			`client/customers/${selectedLocation.id}?search=${search}&page=${page}&sort=${sort}&desc=${descending}`
		)
			.then((res) => {
				let temp = [...content];
				res.map((entry) => {
					temp.push(entry);
				});
				
				setContent(temp);
				setReadyToFetch(false);
				setLoading(false);

				if (res.length < ONE_PAGE_LIMTI) {
					setTotalPages(page);
				} else {
					setTotalPages(page + 1);
				}
			})
			.catch(() => {
				// console.log(error);
				setLoading(false);
			});
	}

	function downloadCustomerList() {
		setLoadingUserCsv(true);
		ApiCall(
			"GET",
			`client/customers/${selectedLocation.id}?sort=email&desc=false`
		)
			.then((res) => {
				const filteredData = res.map(
					({ name, email, phone, order_count }) => ({
						name,
						email,
						phone,
						order_count
					})
				);
				let headers = [
					t("clients.username"),
					t("clients.user_email"),
					t("clients.user_phone"),
					t("clients.user_orders")
				];

				if (fileType === "excel") {
					downloadExcelFromJson(
						headers,
						filteredData,
						t("common.customers") + "_" + selectedLocation.name
					);
				} else if (fileType === "csv") {
					downloadCsvFromJson(
						headers,
						filteredData,
						t("common.customers") + "_" + selectedLocation.name
					);
				}

				setLoadingUserCsv(false);
			})
			.catch((error) => {
				console.log(error);
				setLoadingUserCsv(false);
			});
	}

	useEffect(() => {

		if (selectedLocation == null) {
			return;
		}

		if (readyToFetch) {
			fetchClientData();
		}
	}, [readyToFetch, selectedLocation]);

	const handleChange = (text) => {
		const onlyLowerCase = text.target.value.toLowerCase();
		setSearch(onlyLowerCase);
	};

	useEffect(() => {
		if (page == null) {
			setPage(1);
			return;
		}
		setPage(null);
	}, [search, selectedLocation, sort, descending]);

	useEffect(() => {
		handleTitle(t("orders.clients_label"));
	}, []);

	useEffect(() => {
		if (page === null) {
			setPage(1);
			return;
		}
		setLoading(true);
		setContent([]);
		const delay = 500;
		const delayDebounceFn = setTimeout(() => {
			setReadyToFetch(true);
		}, delay);

		return () => clearTimeout(delayDebounceFn);
	}, [page]);

	useEffect(() => {
		const boxHeightT =
			81 * content.length +
			(content.length === 0 ? 80 : 2)
			+ 80;

		setBoxHeight(boxHeightT);
	}, [loading, content]);

	return (
		<LandingAnimation>
			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					justifyContent: isMobile ? "center" : "end",
					marginBottom: "20px	"
					// width: "100%"
				}}
			>
				<Select
					value={fileType}
					onChange={(event) => setFileType(event.target.value)}
					sx={{
						width: isMobile ? "100%" : "120px",
						height: "46px",
						backgroundColor: ColorScheme.background,
						border: "1px solid " + ColorScheme.secondary,
						marginRight: isDesktop && "10px",
						color: ColorScheme.secondary,
						"& .MuiSvgIcon-root": {
							color: ColorScheme.secondary
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							borderColor: ColorScheme.transparent
						},
						marginBottom: isMobile && "20px",
						cursor: "pointer"
					}}
				>
					<MenuItem value="csv">CSV</MenuItem>
					<MenuItem value="excel">Excel</MenuItem>
				</Select>
				<Button
					disabled={loadingUserCsv}
					variant="contained"
					onClick={() => downloadCustomerList()}
					sx={{
						height: "46px",
						width: isMobile ? "100%" : "200px",
						/* py: "30px",
						px: "35px", */
						textTransform: "none",
						backgroundColor: ColorScheme.primary,
						"&:hover": {
							backgroundColor: ColorScheme.primary
						}
					}}
				>
					{loadingUserCsv && (
						<Spinner small color={ColorScheme.secondary} />
					)}
					{!loadingUserCsv && (
						<Typography style={{ fontSize: "12px" }}>
							{t("buttons.load_client_csv")}
						</Typography>
					)}
				</Button>
			</div>
			<div style={{ position: "relative" }}>
				<TextField
					fullWidth
					variant="filled"
					label={t("textfield_labels.search")}
					InputLabelProps={{ style: { color: ColorScheme.darkGrey } }}
					inputProps={{
						style: {
							color: ColorScheme.black,
							backgroundColor: ColorScheme.secondary,
							borderRadius: "4px 4px 0px 0px",
							border: "0px"
						}
					}}
					onChange={handleChange}
				/>

				<ClientSortingOptions
					sort={sort}
					setSort={setSort}
					descending={descending}
					setDescending={setDescending}
				/>

				<Box
					sx={{
						height: boxHeight + "px",
						backgroundColor: ColorScheme.secondary,
						alignItems: "center",
						justifyContent: "center",
						display: "flex",
						borderBottomLeftRadius: "4px",
						borderBottomRightRadius: "4px",
						width: "100%",
						transition: "all .2s ease-out",
						overflow: "hidden",
						flexDirection: "column"
					}}
				>
					{content.length === 0 && !loading && !search && (
						<div style={{ flex: 1.5 }} />
					)}

					{content.length === 0 && !loading && (
						<Typography sx={{ color: ColorScheme.black }}>
							{search !== ""
								? t("clients.no_users_found")
								: page === 1
									? t("clients.no_users_yet")
									: t("clients.no_more_users")}
						</Typography>
					)}
					<div
						style={{
							backgroundColor: ColorScheme.secondary,
							borderBottomLeftRadius: "4px",
							borderBottomRightRadius: "4px",
							top: "100px",
							width: "100%"
						}}
					>
						{content.map((entry, i) => (
							<ClientCard
								setDialogUser={setDialogUser}
								key={i}
								user={entry}
								location={selectedLocation}
								isLast={i === content.length - 1}
							/>
						))}
					</div>
					{loading && (
						<div style={{height: "80px", display: "flex", justifyContent: "center", alignItems: "center"}}>
						<Spinner small color={ColorScheme.primary} />
						</div>
					)}
					{content.length === 0 && !loading && !search && (
						<div style={{ flex: 1 }} />
					)}
					<div style={{height: "80px"}}>
					<PagingIndicator totalPages={totalPages} maxButtons={1} currentPage={page} onPageChange={setPage}/>
					</div>
				</Box>
				<ClientOrders
					dialogOpen={dialogUser !== null}
					closeDialog={setDialogUser}
					user={dialogUser}
					handleRefresh={() => setReadyToFetch(true)}
					location={selectedLocation}
				/>
			</div>
		</LandingAnimation>
	);
};
